import { Injectable } from '@angular/core';
import { Store, select } from '@ngrx/store';
import { map, tap } from 'rxjs/operators';
import {
  getGlobalToggleSelector,
  getGlobalToggle,
  ToggleIdentifier,
} from '@mhe/ol-platform/global-toggles';
import { BehaviorSubject, Observable, of } from 'rxjs';

class Identifier implements ToggleIdentifier {
  private readonly FEATURE_OWNER = 'mhereaderapi';

  owner: string;
  name: string;

  initialState: boolean;

  subject: BehaviorSubject<boolean>;
  observable: Observable<boolean>;

  outsideObservable: Observable<boolean>;

  constructor(name: string, initialState: boolean) {
    this.owner = this.FEATURE_OWNER;
    this.name = name;

    this.initialState = initialState;
    this.subject = new BehaviorSubject<boolean>(initialState);
    this.observable = this.subject.asObservable();
  }
}

/**
 * Feature Flags for Reader UI
 *
 * This uses Global Toggles from OL Platform.
 * Flags are defined and controlled via Internal Admin Tools.
 * Flags are mapped and given initial state here.
 *
 * When Reader UI is launched this service will start up and
 * call the external service and get the configured state of our
 * flags.
 *
 * Depending on loading speed your component/code may read the
 * initial state rather than the configured state. Keep that in
 * mind when you are wrapping your component/code with a flag.
 *
 * Local overrides are possible:
 *
 * window.overrideGlobalToggle({owner: 'mhereaderapi', name: 'ai_reader_ui', value: true});
 *
 * Local overrides must be executed AFTER Reader UI is loaded and are NOT persisted.
 */
@Injectable()
export class FeatureTogglesService {
  // map of toggles at the external service
  private readonly identifiers: Identifier[] = [
    // fallback to true if the toggle is not found or cannot be loaded
    new Identifier('ai_reader_ui', true),

    // Math in RS
    new Identifier('math_in_rs', true),

    // Maintenance Banner
    new Identifier('maintenance_notification', false),

    // Translation Tool
    new Identifier('translation_tool', false),

    // Read Aloud + Widget Text
    new Identifier('read_aloud_widget_text', false),

    // Use TTS API
    new Identifier('tts_api', false),

    /**
     * Material Expansion Panel Series
     * These toggles are for projects which replace NGX-Bonsai usage with Material components.
     */

    // TOC
    new Identifier('material_tree_toc', false),

    // Annotations
    new Identifier('material_annotations', false),

    // ePub Search
    new Identifier('material_search_classic', false),

    // FA4 Topics
    new Identifier('material_fa4_topics', false),

    // FA4 Search
    new Identifier('material_fa4_search', false),
  ];

  // ready-to-use observables for use in components and templates
  public isAiReaderEnabled$ = this.getObservable('ai_reader_ui');
  public isMathInRsEnabled$ = this.getObservable('math_in_rs');
  public isMaintenanceNotificationEnabled$ = this.getObservable('maintenance_notification');
  public isTranslationToolEnabled$ = this.getObservable('translation_tool');
  public isReadAloudWidgetTextEnabled$ = this.getObservable('read_aloud_widget_text');
  public isTtsApiEnabled$ = this.getObservable('tts_api');
  public materialTreeToc$ = this.getObservable('material_tree_toc');
  public materialAnnotations$ = this.getObservable('material_annotations');
  public materialSearchClassic$ = this.getObservable('material_search_classic');
  public materialFA4Topics$ = this.getObservable('material_fa4_topics');
  public materialFA4Search$ = this.getObservable('material_fa4_search');

  constructor(
    private readonly store: Store<any>,
  ) {
    // setup the mapped identifiers, results in a cold observable
    this.identifiers.forEach((identifier) => {
      identifier.outsideObservable = store.pipe(
        // select from the global-toggles feature store
        select(getGlobalToggleSelector({ owner: identifier.owner, name: identifier.name })),
        // make use of our defined initial state
        map((toggle) => toggle?.value ?? identifier.initialState),
        // update the subject for observers
        tap((val) => identifier.subject.next(val)),
      );

      // make the observable hot to support initial state
      identifier.outsideObservable.subscribe();

      // trigger the global-toggles action so values are retrieved from the external service
      this.store.dispatch(getGlobalToggle({ owner: identifier.owner, name: identifier.name }));
    });
  }

  public getIdentifier(name: string): Identifier | undefined {
    return this.identifiers.find(identifier => identifier.name === name);
  }

  public getObservable(name: string): Observable<boolean | null> {
    const identifier = this.getIdentifier(name);
    return identifier ? identifier.observable : of(null);
  }
}
